import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, COMPOUND_OPTIONS, PAYBACK_OPTIONS, effectiveAnnual } from './_finHelpers';

// Repayment Calculator — two modes: repay within fixed time OR with fixed installment
export default function RepaymentCalc() {
  const [balance, setBalance] = useState(10000);
  const [rate, setRate] = useState(10);
  const [compound, setCompound] = useState('monthly');
  const [payback, setPayback] = useState('every-month');
  const [mode, setMode] = useState('time'); // 'time' | 'installment'
  const [years, setYears] = useState(5);
  const [months, setMonths] = useState(0);
  const [installment, setInstallment] = useState(200);

  const paybackOpt = PAYBACK_OPTIONS.find(o => o.key === payback) || PAYBACK_OPTIONS[4];
  const paybackPpy = paybackOpt.ppy;

  const effRate = useMemo(() => {
    const eff = effectiveAnnual(rate, compound);
    return Math.pow(1 + eff, 1/paybackPpy) - 1;
  }, [rate, compound, paybackPpy]);

  const c = useMemo(() => {
    if (mode === 'time') {
      const n = Math.round((years*12 + months) * (paybackPpy / 12));
      const M = effRate === 0 ? balance/n : (balance * effRate * Math.pow(1+effRate, n))/(Math.pow(1+effRate, n) - 1);
      return { M, n, total: M * n, interest: M * n - balance };
    } else {
      const n = -Math.log(1 - balance * effRate / installment) / Math.log(1 + effRate);
      const nMonths = n * (12/paybackPpy);
      return { M: installment, n: Math.ceil(n), total: installment * n, interest: installment * n - balance, years: Math.floor(nMonths/12), months: Math.round(nMonths%12) };
    }
  }, [mode, balance, effRate, years, months, installment, paybackPpy]);

  const perLabel = paybackOpt.label.replace('Every ', '').toLowerCase();

  return (
    <CalcShell look="cnet" title="Repayment Calculator" subtitle="Calculate loan payments for a fixed term, or how long a fixed installment will take to pay off."
      about={<>
        <p>The <b>Repayment Calculator</b> is a general-purpose amortized loan tool. Choose whether you want to solve for the payment amount (given a fixed term) or the term (given a fixed installment).</p>
      </>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Loan balance</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={balance} onChange={e=>setBalance(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Interest rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Compound</label>
            <select value={compound} onChange={e=>setCompound(e.target.value)}>
              {COMPOUND_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          <div className="field"><label>Pay back</label>
            <select value={payback} onChange={e=>setPayback(e.target.value)}>
              {PAYBACK_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label.toLowerCase()}</option>)}
            </select>
          </div>
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-2 text-sm"><input type="radio" name="rmode" checked={mode==='time'} onChange={()=>setMode('time')}/> Repay within a fixed time of</label>
            {mode === 'time' && (
              <div className="grid grid-cols-2 gap-2 ml-6">
                <div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div>
                <div className="flex gap-2"><input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm"><input type="radio" name="rmode" checked={mode==='installment'} onChange={()=>setMode('installment')}/> Repay with a fixed installment</label>
            {mode === 'installment' && (
              <div className="flex gap-2 ml-6"><span className="self-center text-slate-500">$</span><input type="number" value={installment} onChange={e=>setInstallment(+e.target.value||0)} className="flex-1"/></div>
            )}
          </div>
        </div>
        <div>
          {mode === 'time' ? (
            <div className="result-panel mb-4"><div className="result-title">Pay back {perLabel}</div><div className="result-value" data-testid="repay-payment">{fmt(c.M)}</div></div>
          ) : (
            <div className="result-panel mb-4"><div className="result-title">Payoff Time</div><div className="result-value">{c.years} yr {c.months} mo</div></div>
          )}
          <table className="data-table"><tbody>
            <tr><td>Total of {c.n} loan payments</td><td className="text-right font-semibold">{fmt(c.total)}</td></tr>
            <tr><td>Interest</td><td className="text-right text-orange-600">{fmt(c.interest)}</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Principal', value:balance, color:'#2563eb'},{label:'Interest', value:c.interest, color:'#10b981'}]} size={170}/></div>
        </div>
      </div>
      <StepsPanel steps={[
        `Effective per-period rate = ${(effRate*100).toFixed(4)}%`,
        mode === 'time' ? `Payment M = ${fmt(c.M)} for ${c.n} periods` : `Payoff = ${c.n} periods at ${fmt(installment)} each`,
        `Total interest ≈ <b>${fmt(c.interest)}</b>`,
      ]}/>
    </CalcShell>
  );
}
